import {
    CommandId,
    PanelId,
    ToolbarConfig,
    ToolbarContainerId,
    ToolbarContainerState,
    ToolbarId,
    ViewConfigPayload,
    ViewId,
    ViewTemplateId,
    ViewTemplateInitPayload,
    WORKBENCH_VIEW_CONFIG,
    WorkbenchDirection,
    WorkbenchContext,
    WorkbenchViewConfig,
    WorkbenchViewTemplateConfig
} from '../types';
import {ComponentRef, createComponent, EnvironmentInjector, Injectable, Injector} from '@angular/core';
import {BehaviorSubject, merge, Observable, ReplaySubject, skip, Subject, switchMap} from 'rxjs';
import {filter} from 'rxjs/operators';
import {ViewContainerComponent} from '../components/viewcontainers/viewcontainer.component';
import {SplitViewContainerComponent} from '../components/viewcontainers/split-viewcontainer.component';
import {WorkbenchContextService} from './workbench-context.service';
import {WorkbenchKeyValStore} from './workbench-store.service';
import {
    CENTER_VIEW_CONTAINER_ID,
    isNormalViewContainerConfig,
    NormalViewContainerConfig,
    SplitViewContainerConfig,
    ViewContainerId,
    WORKBENCH_SPLITVIEWCONTAINER_CONFIG,
    WORKBENCH_VIEWCONTAINER_CONFIG
} from '../components/viewcontainers/viewcontainers';
import _ from 'lodash';
import {WorkbenchViewComponent} from '../components/viewcontainers/workbench-view.component';
import {PersistenceService} from '@webmate-web-core/shared/services/persistence.service';
import {ProjectId} from '../../../report/report_types';
import {WorkbenchPopoutService} from './workbench-popout.service';
import {InspectorPlugin} from '../../workbench-plugins/inspector/inspector-plugin';
import {WorkbenchModeVisibilityService} from './workbench-mode-visibility.service';

export interface PanelState {
    panelId: PanelId;
    shownViewContainer?: ViewContainerId;
}

export interface NormalViewContainerState {
    type: 'NormalViewContainerState';
    viewContainerId: ViewContainerId;
    viewContainerConfig: NormalViewContainerConfig,
    componentRef: ComponentRef<ViewContainerComponent>;
    parentSplitViewContainer?: ViewContainerId;
    views: ViewId[];
    inPanel?: PanelId;
    activeTab?: ViewId;
    visible: boolean;
}

export interface SplitViewContainerState {
    type: 'SplitViewContainerState';
    viewContainerId: ViewContainerId;
    viewContainerConfig: SplitViewContainerConfig,
    componentRef: ComponentRef<SplitViewContainerComponent>;
    sourceChild: ViewContainerId; // The id of the child that has been there before the splitting started
    parentSplitViewContainer?: ViewContainerId;
    inPanel?: PanelId;
    children: ViewContainerId[];
}

interface SplitViewContainerPersistentState {
    sourceChild: ViewContainerId,
    children: ViewContainerId[],
    viewContainerConfig: SplitViewContainerConfig,
    parentSplitViewContainer?: ViewContainerId
}

export type ViewContainerState = NormalViewContainerState | SplitViewContainerState;

export interface ViewState {
    viewId: ViewId;
    viewComponentRef: ComponentRef<WorkbenchViewComponent>;
    expanded?: boolean;
}

export interface PanelSizes {
    leftWidth: number; // pixels
    leftTopHeight: number; // percent.
    leftBottomHeight: number; // percent
    bottomHeight: number; // pixels
    rightWidth: number; // pixels
    rightTopHeight: number; // percent.
    rightBottomHeight: number; // percent.
    bottomLeftWidth: number; // percent. determines bottomRightWidth
}

/**
 * The PersistentLayoutState is the state that is persisted in the browser (e.g. via local storage or cookies).
 * It contains information about the layout of the workbench, e.g.:
 *  - which view containers are shown in which panels
 *  - which views are shown in which view containers
 */
export interface PersistentLayoutState {
    toolbarCommandBindings: { [toolbarId: string]: ToolbarConfig };
    viewContainerPanelBindings: { [viewContainerId: ViewContainerId]: PanelId };
    viewViewContainerBindings: { [viewId: ViewId]: ViewContainerId };
    visibleViewContainers: ViewContainerId[];
    splitViewContainerHierarchy: { [parent: ViewContainerId]: SplitViewContainerPersistentState }; // Parent is always a splitviewcontainer id, children might be either
    instantiatedViewTemplates: Array<{ viewId: ViewId, templateId: ViewTemplateId, payload: ViewTemplateInitPayload }>;
    normalViewContainerConfigs: { [viewContainerId: ViewContainerId]: NormalViewContainerConfig };
    expandedViews?: ViewId[];
    activeView?: ViewId;
    activeTabs?: { [viewContainerId: ViewContainerId]: ViewId };
    sizes: PanelSizes;
}

@Injectable()
export class WorkbenchLayoutManager {
    // Increase the version for every release with a breaking change in the persistence format.
    // That way, users don't have to clear their caches to get a working, updated layout
    readonly PERSISTENCE_VERSION_PREFIX = 'v4.'
    readonly LAYOUT_PERSISTENCE_PREFIX = 'wm.workbench.layout.' + this.PERSISTENCE_VERSION_PREFIX;

    registeredViewContainers: { [id: ViewContainerId]: NormalViewContainerConfig } = {};
    registeredSplitViewContainers: { [id: ViewContainerId]: SplitViewContainerConfig } = {};

    viewContainerStates: { [id: ViewContainerId]: BehaviorSubject<NormalViewContainerState> } = {};
    splitViewContainerStates: { [id: ViewContainerId]: BehaviorSubject<SplitViewContainerState> } = {};

    panelStates: { [id in PanelId]?: BehaviorSubject<PanelState> } = {};

    registeredViews: { [id: ViewId]: WorkbenchViewConfig<any> } = {};
    viewStates: { [id: ViewId]: BehaviorSubject<ViewState> } = {};
    viewsChanged$ = new ReplaySubject<void>(1);

    registeredViewTemplates: { [id: ViewTemplateId]: WorkbenchViewTemplateConfig<any> } = {};
    templateInitPayloads: { [id: ViewId]: ViewTemplateInitPayload } = {};

    // this is necessary because the order of registrations of Views and ViewContainers is not guaranteed
    viewsToViewContainers: { [viewId: ViewId]: ViewContainerId } = {};

    toolbarContainersStates: { [id: ToolbarContainerId]: BehaviorSubject<ToolbarContainerState> } = {};
    private toolbarConfigs: { [id: ToolbarContainerId]: ToolbarConfig } = {};
    persistentLayoutState: PersistentLayoutState;
    projectId: ProjectId;

    //Default sizes for the workbench panels
    readonly defaultPanelSizes: PanelSizes = {
        leftWidth: 400,
        leftTopHeight: 50,
        leftBottomHeight: 50,
        bottomHeight: 350,
        rightWidth: 400,
        rightTopHeight: 50,
        rightBottomHeight: 50,
        bottomLeftWidth: 50,
    }

    // used for uniquely identifying split view containers
    splitIdCounter = 0;

    readonly ACTIVEVIEWCONTAINER_HISTORY_LENGTH = 5;
    // Most recent at position 0; max length 5
    activeViewContainerHistory: ViewContainerId[] = [];

    activeViewContainerId: ViewContainerId;
    activeViewContainerId$ = new ReplaySubject<ViewContainerId>(1);

    activeViewId: ViewId;
    activeViewId$ = new ReplaySubject<ViewId>(1);

    panelSizes: PanelSizes = { ...this.defaultPanelSizes };

    fixedToolLayout: boolean;
    private currentContext?: WorkbenchContext;
    private suppressPersistence = false;

    constructor(private readonly persistenceService: PersistenceService, private readonly store: WorkbenchKeyValStore,
                private injector: Injector,
                private environmentInjector: EnvironmentInjector,
                private readonly workbenchContextService: WorkbenchContextService,
                private readonly workbenchPopoutService: WorkbenchPopoutService,
                private readonly modeVisibilityService: WorkbenchModeVisibilityService) {
    }

    //
    // Basic Setup
    //

    /**
     * Initialize the workbench layout manager.
     */
    initialize() {
        this.workbenchContextService.subscribeWorkbenchContext$().subscribe(context => {
            const previousMode = this.currentContext?.activeMode.kind;
            this.currentContext = context;
            this.fixedToolLayout = false;
            this.projectId = context.project;
            if (previousMode && previousMode !== context.activeMode.kind) {
                this.applyModeVisibility();
                this.applyToolbarVisibility();
            }
        });
    }

    preparePanels() {
        for (const panelId of ['left-top', 'left-bottom', 'bottom', 'center-center', 'right'] as PanelId[]) {
            this.panelStates[panelId] = new BehaviorSubject<PanelState>({
                panelId: panelId,
                shownViewContainer: undefined
            });
        }
    }

    initializePersistence() {
        this.persistentLayoutState = this.loadPersistentLayoutState();
    }

    initializePanels() {
        // place view containers in panels according to the persistent layout state or default configuration.
        const persistentLayoutState = this.persistentLayoutState;

        this.persistentLayoutState.instantiatedViewTemplates.forEach(({viewId, templateId, payload}) => {
            if (this.registeredViewTemplates[templateId]) {
                this.instantiateAndShowViewTemplate(templateId, payload, false, true);
            }
        });

        const bindings = persistentLayoutState.viewViewContainerBindings;
        Object.entries(bindings).forEach(([viewId, viewContainerId]) => {
            // TODO check if this is sthill correct
            if (this.registeredViews[viewId] && this.registeredViewContainers[viewContainerId]) {
                this.viewsToViewContainers[viewId] = viewContainerId;
            }
        });

        // With profiles removed, initial toolbar placement is derived from the shared layout:
        // each view container contributes its toggle command to the toolbar closest to its default panel.
        if (!this.fixedToolLayout && !this.hasPersistentLayout()) {
            const toolbarToCommandMap: Map<ToolbarId, CommandId[]> = new Map();
            Object.values<NormalViewContainerConfig>(this.registeredViewContainers).forEach(config => {
                const desiredToolbarContainer = this.mapPanelIdToTClosestToolbarId(config.defaultPanel || 'left-top');
                if (!!config.toolbarCommandId) {
                    let oldValues = toolbarToCommandMap.get(desiredToolbarContainer) || [];
                    toolbarToCommandMap.set(desiredToolbarContainer, oldValues.concat([config.toolbarCommandId]));
                }
            });
            toolbarToCommandMap.forEach((value: CommandId[], key: ToolbarId) => {
                this.registerToolbar(key, value);
            });
        }

        persistentLayoutState.visibleViewContainers.forEach(viewContainerId => {
            if (!this.isViewContainerAvailableInCurrentMode(viewContainerId)) {
                return;
            }
            if (this.registeredViewContainers[viewContainerId]) {
                const panelId = this.persistentLayoutState.viewContainerPanelBindings[viewContainerId] ||
                    this.registeredViewContainers[viewContainerId].defaultPanel || 'left-top';
                this.showViewContainerInPanel(viewContainerId, panelId);
            } else if (this.registeredSplitViewContainers[viewContainerId]) {
                this.showSplitViewContainerInPanel(viewContainerId, this.persistentLayoutState.viewContainerPanelBindings[viewContainerId] || 'center-center');
            } else {
                console.warn(`View container with id ${viewContainerId} not registered.`);
            }
        });

        Object.entries(persistentLayoutState.activeTabs || {}).forEach(([viewContainerId, viewId]) => {
            if (this.viewContainerStates[viewContainerId] && this.viewStates[viewId]) {
                this.viewContainerStates[viewContainerId].next({
                    ...this.viewContainerStates[viewContainerId].getValue(),
                    activeTab: viewId
                });
            }
        });

        // Restore the per-view expansion state. When expandedViews has been persisted it is an
        // authoritative snapshot of every expanded view at save time, so any registered view that is
        // not contained in it must be collapsed here - even if it is expanded by default (e.g. the
        // inspector). Otherwise a view with expandedByDefault could never be persisted as collapsed.
        if (persistentLayoutState.expandedViews) {
            const expandedViews = new Set(persistentLayoutState.expandedViews);
            Object.keys(this.viewStates).forEach(viewId => {
                const viewState = this.viewStates[viewId].getValue();
                const expanded = expandedViews.has(viewId);
                if (viewState.expanded !== expanded) {
                    this.viewStates[viewId].next({
                        ...viewState,
                        expanded: expanded
                    });
                }
            });
            this.viewsChanged$.next();
        }

        if (persistentLayoutState.activeView && this.registeredViews[persistentLayoutState.activeView]) {
            this.showView(persistentLayoutState.activeView, true);
        }
        this.subscribeToPanelEvents$().subscribe(() => {
            this.persistLayoutState();
        });

        this.subscribeToToolbarContainerEvents$(undefined, true).subscribe(() => {
            this.persistLayoutState();
        });

        this.subscribeToViewEvents$().subscribe(() => {
            this.persistLayoutState(true);
        });

        this.subscribeToPanelEvents$(undefined, false).subscribe(panelState => {
            if (!!panelState.shownViewContainer) {
                const viewContainerState = this.viewContainerStates[panelState.shownViewContainer]?.getValue();
                if (!!viewContainerState && viewContainerState.type === 'NormalViewContainerState') {
                    this.viewContainerStates[panelState.shownViewContainer].next({
                        ...viewContainerState,
                        visible: true
                    });
                }
            }
        });

        this.applyToolbarVisibility();
        // Required generic view containers are only force-shown on an actual mode transition
        // (see applyModeVisibility). On initial load, respect the persisted visibility state.
    }

    initializeToolbarContainers() {
        for (const toolbarContainerId of ['left-top', 'left-bottom', 'top-left', 'right'] as ToolbarContainerId[]) {
            this.toolbarContainersStates[toolbarContainerId] = new BehaviorSubject<ToolbarContainerState>({
                id: toolbarContainerId
            });
        }
    }

    //
    // Persistence
    //

    getLayoutKey(): string {
        return this.LAYOUT_PERSISTENCE_PREFIX + this.projectId
    }

    private loadPersistentLayoutState(): PersistentLayoutState {
        const result: PersistentLayoutState = this.persistentLayoutState = {
            toolbarCommandBindings: {},
            viewContainerPanelBindings: {},
            viewViewContainerBindings: {},
            splitViewContainerHierarchy: {},
            normalViewContainerConfigs: {},
            instantiatedViewTemplates: [],
            visibleViewContainers: [],
            sizes: { ...this.defaultPanelSizes },
        };

        const persisted = this.persistenceService.getObjectItem(this.getLayoutKey()) as PersistentLayoutState;
        Object.entries<ToolbarConfig>(persisted?.toolbarCommandBindings || {}).forEach(([toolbarId, config]) => {
            result.toolbarCommandBindings[toolbarId] = config;
            this.registerToolbar(toolbarId, config);
        });

        // load persisted split view hierarchy
        result.normalViewContainerConfigs = persisted?.normalViewContainerConfigs ?? {};

        Object.entries<SplitViewContainerPersistentState>(persisted?.splitViewContainerHierarchy || {}).forEach(([id, state]) => {
            // register all split view containers from persistence
            result.splitViewContainerHierarchy[id] = state;
            this.registerSplitViewContainer(state.viewContainerConfig, state.children, state.sourceChild, state.parentSplitViewContainer);
        });

        Object.keys(this.registeredSplitViewContainers).forEach(splitViewContainerId => {
            // verify and setup the structure of normal view containers
            const state = this.splitViewContainerStates[splitViewContainerId].getValue();
            state.children?.forEach(childId => {
                if (this.registeredSplitViewContainers[childId]) {
                    // split views are guaranteed to be set up correctly at this point
                    return;
                }
                if (this.registeredViewContainers[childId]) {
                    // set the parent split view container
                    this.viewContainerStates[childId].next({
                        ...this.viewContainerStates[childId].getValue(),
                        parentSplitViewContainer: splitViewContainerId
                    });
                } else if (result?.normalViewContainerConfigs[childId]) {
                    // view containers that do not exist yet, set it up if we have the corresponding config available
                    this.registerViewContainer(result?.normalViewContainerConfigs[childId], splitViewContainerId);
                }
            });
        });

        Object.entries<ViewContainerId>(persisted?.viewViewContainerBindings || {}).forEach(([viewId, viewContainerId]) => {
            // Check is redundant as it is performed when processing the loaded layout
            // if (this.registeredViews[viewId] && this.registeredViewContainers[viewContainerId]) {
            result.viewViewContainerBindings[viewId] = viewContainerId;
            this.viewsToViewContainers[viewId] = viewContainerId;
            // }
        });

        (persisted?.instantiatedViewTemplates || []).forEach(({viewId, templateId, payload}) => {
            if (this.registeredViewTemplates[templateId]) {
                result.instantiatedViewTemplates.push({viewId, templateId, payload});
            }
        });

        Object.entries<ViewContainerId>(persisted?.viewContainerPanelBindings || {}).forEach(([viewContainerId, panelId]) => {
            if ((this.registeredViewContainers[viewContainerId] || this.registeredSplitViewContainers[viewContainerId]) && this.panelStates[panelId]) {
                result.viewContainerPanelBindings[viewContainerId] = panelId as PanelId;
            }
        });
        (persisted?.visibleViewContainers || [CENTER_VIEW_CONTAINER_ID, InspectorPlugin.ViewContainerId]).forEach(viewContainerId => {
            if (this.registeredViewContainers[viewContainerId] || this.registeredSplitViewContainers[viewContainerId]) {
                result.visibleViewContainers.push(viewContainerId);
            }
        });

        result.expandedViews = persisted?.expandedViews;
        result.activeTabs = persisted?.activeTabs;
        result.activeView = persisted?.activeView;
        if (persisted?.sizes) {
            result.sizes = this.panelSizes = persisted.sizes;
        }

        return result;
    }

    private persistLayoutState(updateViews: boolean = false) {
        if (this.suppressPersistence) {
            return;
        }
        this.persistentLayoutState.toolbarCommandBindings = {};
        for (const container of Object.keys(this.toolbarContainersStates)) {
            const toolbarConfig = this.toolbarConfigs[container];
            if (!!toolbarConfig && toolbarConfig.length > 0) {
                this.persistentLayoutState.toolbarCommandBindings[container] = toolbarConfig;
            }
        }
        this.persistentLayoutState.viewContainerPanelBindings = {};
        this.persistentLayoutState.normalViewContainerConfigs = {};
        this.persistentLayoutState.activeTabs = {};
        for (const container of Object.keys(this.viewContainerStates)) {
            const viewContainerState = this.viewContainerStates[container].getValue();
            this.persistentLayoutState.normalViewContainerConfigs[viewContainerState.viewContainerId] = viewContainerState.viewContainerConfig;
            if (viewContainerState.inPanel) {
                this.persistentLayoutState.viewContainerPanelBindings[viewContainerState.viewContainerId] = viewContainerState.inPanel;
            }
            if (viewContainerState.activeTab) {
                this.persistentLayoutState.activeTabs[viewContainerState.viewContainerId] = viewContainerState.activeTab;
            }
        }

        this.persistentLayoutState.splitViewContainerHierarchy = {};
        for (const container of Object.keys(this.splitViewContainerStates)) {
            const viewContainerState = this.splitViewContainerStates[container].getValue();
            if (viewContainerState.inPanel) {
                this.persistentLayoutState.viewContainerPanelBindings[viewContainerState.viewContainerId] = viewContainerState.inPanel;
            }
            this.persistentLayoutState.splitViewContainerHierarchy[container] = {
                sourceChild: viewContainerState.sourceChild,
                children: viewContainerState.children,
                viewContainerConfig: viewContainerState.viewContainerConfig,
                parentSplitViewContainer: viewContainerState.parentSplitViewContainer
            };
        }

        const hiddenUnavailableViewContainers = this.persistentLayoutState.visibleViewContainers
            .filter(viewContainerId => !this.isViewContainerShown(viewContainerId) && !this.isViewContainerAvailableInCurrentMode(viewContainerId));
        this.persistentLayoutState.visibleViewContainers = [];
        for (const panel of Object.keys(this.panelStates)) {
            const panelState = this.panelStates[panel].getValue();
            if (panelState.shownViewContainer) {
                this.persistentLayoutState.visibleViewContainers.push(panelState.shownViewContainer);
            }
        }
        this.persistentLayoutState.visibleViewContainers = _.uniq([
            ...this.persistentLayoutState.visibleViewContainers,
            ...hiddenUnavailableViewContainers
        ]);

        this.persistentLayoutState.instantiatedViewTemplates = [];
        for (const viewId of Object.keys(this.registeredViews)) {
            const view = this.registeredViews[viewId];
            if (view.fromTemplate) {
                this.persistentLayoutState.instantiatedViewTemplates.push({
                    viewId,
                    templateId: view.fromTemplate,
                    payload: this.templateInitPayloads[view.id]
                });
            }
        }

        this.persistentLayoutState.viewViewContainerBindings = this.viewsToViewContainers;

        if (updateViews) {
            this.persistentLayoutState.expandedViews = [];
            for (const viewId of Object.keys(this.viewStates)) {
                const viewState = this.getViewState(viewId);
                if (viewState.expanded) {
                    this.persistentLayoutState.expandedViews.push(viewId);
                }
            }
        }

        this.persistentLayoutState.activeView = this.activeViewId;
        this.persistentLayoutState.sizes = this.panelSizes;
        this.persistenceService.setObjectItem(this.getLayoutKey(), this.persistentLayoutState);
    }

    private hasPersistentLayout() {
        return !!(this.persistenceService.getObjectItem(this.getLayoutKey()) as PersistentLayoutState);
    }

    setPanelSizes(sizes: PanelSizes) {
        this.panelSizes = sizes;
        this.persistLayoutState();
        this.triggerLayoutChangedEvent();
    }

    //
    // Registration
    //

    registerToolbar(id: ToolbarContainerId, toolbar: ToolbarConfig): void {
        this.toolbarConfigs[id] = _.uniq(toolbar);
        this.toolbarContainersStates[id].next({
            id: id,
            config: this.filterToolbarConfig(this.toolbarConfigs[id])
        });
    }

    private setToolbarConfig(id: ToolbarContainerId, toolbar: ToolbarConfig): void {
        this.toolbarConfigs[id] = _.uniq(toolbar);
        this.toolbarContainersStates[id].next({
            id,
            config: this.filterToolbarConfig(this.toolbarConfigs[id])
        });
    }

    /**
     * Register a view container.
     */
    registerViewContainer(viewContainer: NormalViewContainerConfig, parentSplitViewContainer?: ViewContainerId): void {
        if (!viewContainer.allowedPanels) {
            viewContainer.allowedPanels = ['left-top', 'left-bottom', 'bottom', 'center-center'];
        }
        if (!viewContainer.displayMode) {
            viewContainer.displayMode = 'stacked';
        }
        if (viewContainer.splitAllowed === undefined) {
            viewContainer.splitAllowed = false;
        }
        if (viewContainer.moveAllowed === undefined) {
            viewContainer.moveAllowed = true;
        }
        if (viewContainer.hideAllowed === undefined) {
            viewContainer.hideAllowed = true;
        }

        if (!!this.registeredViewContainers[viewContainer.id]) {
            return;
        }

        const componentPortal = this.makeViewContainerComponentRef(viewContainer);

        // calculate the mapping of ViewContainers to Views
        const associatedViews: ViewId[] = [];
        for (const viewId of Object.keys(this.viewsToViewContainers)) {
            if (this.viewsToViewContainers[viewId] === viewContainer.id) {
                associatedViews.push(viewId);
            }
        }

        this.registeredViewContainers[viewContainer.id] = viewContainer;
        this.viewContainerStates[viewContainer.id] = new BehaviorSubject<NormalViewContainerState>({
            type: 'NormalViewContainerState',
            viewContainerId: viewContainer.id,
            viewContainerConfig: viewContainer,
            inPanel: undefined, // on registration, the view container is not shown in any panel
            views: associatedViews,
            componentRef: componentPortal,
            visible: false,
            parentSplitViewContainer: parentSplitViewContainer
        });

        this.viewContainerStates[viewContainer.id].subscribe(state => {
            this.store.setValue('viewcontainers.' + viewContainer.id + '.status.visible', !!state.visible);
        });
    }

    registerSplitViewContainer(splitViewContainer: SplitViewContainerConfig, children: ViewContainerId[], sourceChild: ViewContainerId, parentSplitViewContainer?: ViewContainerId): void {
        if (!!this.registeredSplitViewContainers[splitViewContainer.id]) {
            throw new Error(`Split view container with id ${splitViewContainer.id} already registered.`);
        }

        const componentPortal = this.makeSplitViewContainerComponentRef(splitViewContainer);

        this.registeredSplitViewContainers[splitViewContainer.id] = splitViewContainer;
        this.splitViewContainerStates[splitViewContainer.id] = new BehaviorSubject<SplitViewContainerState>({
            type: 'SplitViewContainerState',
            viewContainerId: splitViewContainer.id,
            viewContainerConfig: splitViewContainer,
            children: children,
            sourceChild: sourceChild,
            parentSplitViewContainer: parentSplitViewContainer,
            componentRef: componentPortal,
        });
    }

    private getViewContainerState(viewContainerId: ViewContainerId): ViewContainerState | undefined {
        return this.viewContainerStates[viewContainerId]?.getValue() ?? this.splitViewContainerStates[viewContainerId]?.getValue();
    }

    // From an arbitrary entry point, create a list of all view containers that are part of a hierarchy of SplitViewContainers
    // If the entry point is not part of such a hierarchy, just itself is returned
    private enumerateViewContainersInSplitViewContainerHierarchy(entryPoint: ViewContainerId): ViewContainerId[] {
        let currentState: ViewContainerState = this.getViewContainerState(entryPoint);
        while (currentState.parentSplitViewContainer) {
            currentState = this.getViewContainerState(currentState.parentSplitViewContainer);
        }
        let root = currentState;
        const enumerateChildren = (state: ViewContainerState): ViewContainerId[] => {
            if (state.type === 'NormalViewContainerState') {
                return [state.viewContainerId];
            } else {
                return _.flatten(state.children.map(vcid => this.getViewContainerState(vcid)).map(state => enumerateChildren(state)));
            }
        };
        return enumerateChildren(root);
    }

    registerView<T extends ViewConfigPayload>(view: WorkbenchViewConfig<T>): void {
        this.registeredViews[view.id] = view;

        if (!this.viewsToViewContainers[view.id] && !!view.defaultViewContainer) {
            if (!this.viewContainerStates[view.defaultViewContainer]?.getValue().parentSplitViewContainer) {
                // if default vc is not inside a split view, just use it
                this.viewsToViewContainers[view.id] = view.defaultViewContainer;
            } else {
                // if the vc is in a split view container find a suiting target
                // first enumerate all view containers in the current hierarchy
                const allViewContainersInCurrentHierarchy = this.enumerateViewContainersInSplitViewContainerHierarchy(view.defaultViewContainer);
                // find out which of these has been last active and use it as a target
                const targetViewContainer = this.activeViewContainerHistory.find(vcid => allViewContainersInCurrentHierarchy.includes(vcid));
                this.viewsToViewContainers[view.id] = targetViewContainer ?? view.defaultViewContainer;
            }
        }

        if (!view) {
            console.error(`Trying to register view but no view is given.`);
            return;
        }

        const injector = Injector.create({
            parent: this.injector,
            providers: [
                {provide: WORKBENCH_VIEW_CONFIG, useValue: view},
            ]
        });

        const viewComponentRef = createComponent(view.component, {
            environmentInjector: this.environmentInjector,
            elementInjector: injector
        });

        this.viewStates[view.id] = new BehaviorSubject<ViewState>({
            viewId: view.id,
            viewComponentRef: viewComponentRef,
            expanded: view.expandedByDefault
        });
        this.viewsChanged$.next();

        const associatedViewContainer = this.viewsToViewContainers[view.id];

        // There may be the situation that the view container is not yet registered.
        // In this case, the view will be associated with the view container when it is registered.
        if (this.viewContainerStates[associatedViewContainer]) {
            const viewContainerState = this.viewContainerStates[associatedViewContainer].getValue();
            if (this.viewContainerStates[associatedViewContainer]) {
                this.viewContainerStates[associatedViewContainer].next({
                    ...viewContainerState,
                    views: [...viewContainerState.views, view.id]
                });
            }
        }
    }

    registerViewTemplate<T extends ViewConfigPayload>(template: WorkbenchViewTemplateConfig<T>) {
        this.registeredViewTemplates[template.templateId] = template; //ToDo Template
    }

    instantiateAndShowViewTemplate(templateId: ViewTemplateId, payload: ViewTemplateInitPayload, makeActive: boolean = true, fromPersistence: boolean = false) {
        const template = this.registeredViewTemplates[templateId];
        if (!template) {
            console.warn(`ViewTemplate with id ${templateId} could not be found.`);
            return;
        }
        const viewConfig = template.init(payload);
        viewConfig.pluginId = viewConfig.pluginId || template.pluginId;
        viewConfig.visibility = viewConfig.visibility || template.visibility;
        viewConfig.restoredFromPersistence = fromPersistence;
        if (!this.registeredViews[viewConfig.id]) {
            // If the same view has been created and registered before, don't register it again
            this.registerView(viewConfig);
            this.templateInitPayloads[viewConfig.id] = payload;
        }

        this.showView(viewConfig.id, makeActive);
        if (!fromPersistence) {
            this.persistLayoutState();
        }
    }

    private makeViewContainerComponentRef(viewContainer: NormalViewContainerConfig): ComponentRef<ViewContainerComponent> {
        const injector = Injector.create({
            parent: this.injector,
            providers: [
                {
                    provide: WORKBENCH_VIEWCONTAINER_CONFIG,
                    useValue: viewContainer
                },
            ]
        });
        return createComponent(ViewContainerComponent, {
            environmentInjector: this.environmentInjector,
            elementInjector: injector
        });
    }

    private makeSplitViewContainerComponentRef(viewContainer: SplitViewContainerConfig): ComponentRef<SplitViewContainerComponent> {
        const injector = Injector.create({
            parent: this.injector,
            providers: [
                {
                    provide: WORKBENCH_SPLITVIEWCONTAINER_CONFIG,
                    useValue: viewContainer
                },
            ]
        });
        return createComponent(SplitViewContainerComponent, {
            environmentInjector: this.environmentInjector,
            elementInjector: injector
        });
    }

    //
    // Showing and Hiding
    //

    toggleViewContainer(viewContainerId: ViewContainerId, makeActive: boolean = false) {
        if (this.isViewContainerShown(viewContainerId)) {
            this.hideViewContainer(viewContainerId);
        } else {
            this.showViewContainer(viewContainerId);
            if (makeActive) {
                this.activateViewContainer(viewContainerId);
            }
        }
    }

    /**
     * Show the specified view in a view container. If the associated view container is not shown, it is opened and the view is expanded.
     */
    showView(id: ViewId, makeActive: boolean = true) {
        const viewContainerId = this.viewsToViewContainers[id];
        if (!viewContainerId) {
            throw new Error(`View ${id} not registered in any view container.`);
        }
        if (!this.isViewVisibleInCurrentMode(id)) {
            return;
        }
        this.showViewContainer(viewContainerId);
        if (makeActive) {
            this.activateViewContainer(viewContainerId, id);
        }
    }

    closeView(viewId: ViewId) {
        const viewContainerId = this.viewsToViewContainers[viewId];
        if (!viewContainerId) {
            console.warn(`Trying to close unregistered view ${viewId}.`);
            return;
        }
        const state = this.viewContainerStates[viewContainerId].getValue();
        const newViews = state.views.filter(v => v !== viewId);
        this.viewContainerStates[viewContainerId].next({
            ...state,
            views: newViews,
            activeTab: state.activeTab === viewId ? undefined : state.activeTab
        });

        if (newViews.length === 0 && !state.parentSplitViewContainer) {
            this.hideViewContainer(viewContainerId);
        }
        this.viewStates[viewId].getValue().viewComponentRef.instance.beforeViewClose();
        this.viewStates[viewId].getValue().viewComponentRef.destroy();
        delete this.viewStates[viewId];
        delete this.viewsToViewContainers[viewId];
        delete this.registeredViews[viewId];
        this.persistLayoutState();
    }

    private applyModeVisibility(): void {
        if (!this.persistentLayoutState || _.isEmpty(this.viewContainerStates)) {
            return;
        }

        this.suppressPersistence = true;
        try {
            Object.keys(this.viewContainerStates).forEach(viewContainerId => {
                if (this.isViewContainerShown(viewContainerId) && !this.isViewContainerAvailableInCurrentMode(viewContainerId)) {
                    this.hideViewContainer(viewContainerId);
                }
            });

            this.persistentLayoutState.visibleViewContainers.forEach(viewContainerId => {
                if (!this.isViewContainerShown(viewContainerId) && this.isViewContainerAvailableInCurrentMode(viewContainerId)) {
                    this.showViewContainer(viewContainerId);
                }
            });
            this.refreshViewContainerVisibleViews();
            this.deactivateUnavailableActiveView();
        } finally {
            this.suppressPersistence = false;
        }
        this.ensureRequiredGenericViewContainersVisible();
    }

    private applyToolbarVisibility(): void {
        this.suppressPersistence = true;
        try {
            Object.keys(this.toolbarContainersStates).forEach(toolbarContainerId => {
                const toolbarConfig = this.toolbarConfigs[toolbarContainerId] ||
                    this.toolbarContainersStates[toolbarContainerId].getValue().config || [];
                this.toolbarContainersStates[toolbarContainerId].next({
                    id: toolbarContainerId,
                    config: this.filterToolbarConfig(toolbarConfig)
                });
            });
        } finally {
            this.suppressPersistence = false;
        }
    }

    private filterToolbarConfig(toolbar: ToolbarConfig): ToolbarConfig {
        return toolbar.filter(command => {
            const viewContainerId = this.getViewContainerIdForToolbarCommand(command);
            return !viewContainerId || this.isViewContainerAvailableInCurrentMode(viewContainerId);
        });
    }

    private getViewContainerIdForToolbarCommand(command: CommandId): ViewContainerId | undefined {
        return Object.values(this.registeredViewContainers)
            .find(config => config.toolbarCommandId === command)?.id;
    }

    private ensureRequiredGenericViewContainersVisible(): void {
        if (!this.persistentLayoutState) {
            return;
        }
        let hasShownContainer = false;
        this.getRequiredGenericViewContainersInCurrentMode().forEach(viewContainerId => {
            if (this.registeredViewContainers[viewContainerId] &&
                this.isViewContainerAvailableInCurrentMode(viewContainerId) &&
                !this.isViewContainerShown(viewContainerId)) {
                this.showViewContainer(viewContainerId);
                hasShownContainer = true;
            }
        });
        if (hasShownContainer) {
            this.persistLayoutState();
        }
    }

    private getRequiredGenericViewContainersInCurrentMode(): ViewContainerId[] {
        if (this.currentContext?.activeMode.kind === 'manual-test' || this.currentContext?.activeMode.kind === 'audit') {
            return [InspectorPlugin.ViewContainerId];
        }
        return [];
    }

    private deactivateUnavailableActiveView(): void {
        if (!this.activeViewId || this.isViewVisibleInCurrentMode(this.activeViewId)) {
            return;
        }
        const replacementViewId = Object.values(this.viewContainerStates)
            .map(state => state.getValue())
            .filter(state => this.isViewContainerShown(state.viewContainerId))
            .flatMap(state => state.views)
            .find(viewId => this.isViewVisibleInCurrentMode(viewId));

        if (replacementViewId) {
            this.showView(replacementViewId, true);
        }
    }

    private isViewVisibleInCurrentMode(viewId: ViewId): boolean {
        const view = this.registeredViews[viewId];
        return !view || this.modeVisibilityService.isViewKindVisible(view, this.currentContext);
    }

    private refreshViewContainerVisibleViews(): void {
        Object.values(this.viewContainerStates).forEach(state$ => {
            const state = state$.getValue();
            const visibleViews = state.views.filter(viewId => this.isViewVisibleInCurrentMode(viewId));
            state$.next({
                ...state,
                activeTab: state.activeTab && visibleViews.includes(state.activeTab)
                    ? state.activeTab
                    : visibleViews[0]
            });
        });
    }

    private isViewContainerAvailableInCurrentMode(viewContainerId: ViewContainerId): boolean {
        const associatedViews = _.uniq([
            ...(this.viewContainerStates[viewContainerId]?.getValue().views || []),
            ...Object.entries(this.viewsToViewContainers)
                .filter(([, associatedViewContainerId]) => associatedViewContainerId === viewContainerId)
                .map(([viewId]) => viewId)
        ]);
        if (associatedViews.length === 0) {
            return true;
        }
        return associatedViews
            .map(viewId => this.registeredViews[viewId])
            .filter((view): view is WorkbenchViewConfig<any> => !!view)
            .some(view => this.modeVisibilityService.isViewKindVisible(view, this.currentContext));
    }

    showViewContainer(viewContainerId: ViewContainerId) {
        // A view container must only be shown in one panel at a time.
        // If it is already shown in a panel, we do nothing.
        // If it's not shown, we displace the current view container in the panel and show the new one.

        const viewContainerConfig = this.registeredViewContainers[viewContainerId];
        if (!viewContainerConfig) {
            return;
        }
        if (!this.isViewContainerAvailableInCurrentMode(viewContainerId)) {
            return;
        }

        // check if the view container is already shown in a panel (return if it is)
        if (this.isViewContainerShown(viewContainerId)) {
            return;
        }

        // if, according to the persistent layout state, the view container is shown in a panel, and not part of a split view, we show it there
        if (!this.viewContainerStates[viewContainerId].getValue().parentSplitViewContainer) {
            let targetPanelId: PanelId = this.persistentLayoutState.viewContainerPanelBindings[viewContainerId] ||
                viewContainerConfig.defaultPanel;
            this.showViewContainerInPanel(viewContainerId, targetPanelId);
        }
        // notify all views about a changed layout
        this.triggerLayoutChangedEvent();
    }

    hideViewContainer(viewContainerId: ViewContainerId) {
        const viewContainerState = this.viewContainerStates[viewContainerId].getValue();

        // activate another view container in the panel
        if (this.activeViewContainerId === viewContainerId) {
            this.activateRelatedViewContainer(viewContainerState);
        }
        if (!!viewContainerState.parentSplitViewContainer) {
            // if the view container is a child of a split view container, we remove it from the split view container
            const parentSplitViewContainerState = this.splitViewContainerStates[viewContainerState.parentSplitViewContainer].getValue();
            let newChildren: ViewContainerId[];
            let toRemove: ViewContainerId;
            if (parentSplitViewContainerState.sourceChild === viewContainerId) {
                // if the child we try to remove is the source child, move all views from one of the other childs to the source child and remove that one instead
                // - find next best view container to replace the source one
                toRemove = parentSplitViewContainerState.children.find(childId => childId !== parentSplitViewContainerState.sourceChild);
                // - move all views from the chosen one to the source
                for (const viewId of Object.keys(this.viewsToViewContainers)) {
                    if (this.viewsToViewContainers[viewId] === toRemove) {
                        this.moveViewToViewContainer(viewId, parentSplitViewContainerState.sourceChild);
                    }
                }
                // - remove the chosen one
            } else {
                // if we try to remove some other child, just remove it
                toRemove = viewContainerId;
            }
            newChildren = parentSplitViewContainerState.children.filter(childId => childId !== toRemove);
            delete this.registeredViewContainers[toRemove];
            delete this.viewContainerStates[toRemove];

            this.splitViewContainerStates[viewContainerState.parentSplitViewContainer].next({
                ...parentSplitViewContainerState,
                children: newChildren
            });

            this.maintainSplitViewContainers();
        } else {
            const panelId = viewContainerState.inPanel;
            if (!panelId) {
                console.warn(`View container ${viewContainerId} is not shown in any panel.`);
                return;
            }

            this.panelStates[panelId].next({
                panelId: panelId,
                shownViewContainer: undefined,
            });
            this.viewContainerStates[viewContainerId].next({
                ...this.viewContainerStates[viewContainerId].getValue(),
                visible: false
            });
        }
        // notify all views about a changed layout
        this.triggerLayoutChangedEvent();
    }

    private activateRelatedViewContainer(viewContainerState: NormalViewContainerState) {
        // TODO: implement more intuitive strategy for choosing the new active view container: backtrack some list of recent activations
        //  until you find a view container that is still visible, or choose one that has least number of children
        const newActiveViewContainer = Object.values(this.viewContainerStates)
            .map(state => state.getValue())
            // heuristic for choosing the newly activated view container: choose the first one matching the following criteria:
            // - has the same parent split view container (sibling to the hidden view container)
            // - is shown in the same panel
            // - is a child of a split view container
            // - is contained in the center panel
            .filter(state => state.viewContainerId !== viewContainerState.viewContainerId && this.isViewContainerShown(state.viewContainerId))
            .filter(state => (!!viewContainerState.parentSplitViewContainer && state.parentSplitViewContainer === viewContainerState.parentSplitViewContainer) ||
                (!!viewContainerState.inPanel && state.inPanel === viewContainerState.inPanel) ||
                !!state.parentSplitViewContainer ||
                state.inPanel === 'center-center')[0];

        if (!!newActiveViewContainer) {
            this.activateViewContainer(newActiveViewContainer?.viewContainerId);
        }
    }

    activateViewContainer(viewContainerId: ViewContainerId, viewId?: ViewId) {
        this.activeViewContainerId$.next(viewContainerId);
        this.activeViewContainerId = viewContainerId;
        // update history while removing duplicates
        this.activeViewContainerHistory.unshift(this.activeViewContainerId);
        this.activeViewContainerHistory = _.uniq(this.activeViewContainerHistory).slice(0, this.ACTIVEVIEWCONTAINER_HISTORY_LENGTH);
        if (viewId) {
            this.activeViewId$.next(viewId);
            this.activeViewId = viewId;
        }
    }

    showViewContainerInPanel(viewContainerId: ViewContainerId, panelId: PanelId) {
        if (!this.panelStates[panelId]) {
            throw new Error(`Panel with id ${panelId} not registered.`);
        }
        if (!this.viewContainerStates[viewContainerId]) {
            throw new Error(`No state for view container with id ${viewContainerId}.`);
        }
        if (!this.isViewContainerAvailableInCurrentMode(viewContainerId)) {
            return;
        }

        const viewContainerState = this.viewContainerStates[viewContainerId].getValue();
        const oldPanelId = viewContainerState.inPanel || 'left-top';
        let closestToolbarContainer = this.mapPanelIdToTClosestToolbarId(oldPanelId);

        // The ViewContainer was already shown in another Panel. Remove it and its commands from there.
        if (oldPanelId && this.panelStates[oldPanelId].getValue().shownViewContainer === viewContainerId) {
            // If the layout is not fixed we move the toggle button with the panel by first removing it from the current toolbar container
            if (!this.fixedToolLayout && !!this.toolbarContainersStates[closestToolbarContainer]) {
                const command = viewContainerState.viewContainerConfig.toolbarCommandId;
                // Remove the toolbar command from the toolbar of the old panel
                if (command) {
                    this.setToolbarConfig(
                        closestToolbarContainer,
                        (this.toolbarConfigs[closestToolbarContainer] || []).filter(id => id !== command)
                    );
                }
            }
            // Clear the panel, the view container was shown before
            this.panelStates[oldPanelId].next({
                panelId: oldPanelId,
                shownViewContainer: undefined,
            });
        }

        // if there already is a view container in the target panel, we hide it
        const targetPanelExistingViewContainerId = this.panelStates[panelId].getValue().shownViewContainer;
        if (targetPanelExistingViewContainerId) {
            if (!!this.splitViewContainerStates[targetPanelExistingViewContainerId]) {
                // the panel contains a split view container, which is not required to be hidden
                this.splitViewContainerStates[targetPanelExistingViewContainerId].next({
                    ...this.splitViewContainerStates[targetPanelExistingViewContainerId].getValue(),
                    children: [],
                    inPanel: undefined
                });
                this.splitViewContainerStates[targetPanelExistingViewContainerId].complete();
            } else {
                this.hideViewContainer(targetPanelExistingViewContainerId);
            }
        }
        this.viewContainerStates[viewContainerId].next({
            ...this.viewContainerStates[viewContainerId].getValue(),
            parentSplitViewContainer: undefined, // at this point it's guaranteed that the view container is not a child of a split view container
            inPanel: panelId
        });

        // Here we find the closest toolbar container to the new panel position and then adding the corresponding button to it
        closestToolbarContainer = this.mapPanelIdToTClosestToolbarId(panelId);
        if (!this.fixedToolLayout && !!this.toolbarContainersStates[closestToolbarContainer]) {
            const command = viewContainerState.viewContainerConfig.toolbarCommandId;
            if (command) {
                this.setToolbarConfig(closestToolbarContainer, [
                    ...(this.toolbarConfigs[closestToolbarContainer] || []),
                    command
                ]);
            }
        }
        this.panelStates[panelId].next({
            panelId: panelId,
            shownViewContainer: viewContainerId,
        });
    }

    moveViewToDirection(originViewContainer: ViewContainerId, viewId: ViewId, direction: WorkbenchDirection) {
        const viewContainerState = this.viewContainerStates[originViewContainer].getValue();
        const viewContainerConfig = viewContainerState.viewContainerConfig;
        if (!isNormalViewContainerConfig(viewContainerConfig)) {
            throw new Error(`View container ${originViewContainer} is not a normal view container and cannot be moved.`);
        }

        if (direction === 'newWindow') {
            const viewConfig = this.registeredViews[viewId];
            if (viewConfig.popoutConfig) {
                this.workbenchPopoutService.openDeviceConsolePopout(viewConfig.popoutConfig);
            }
            return;
        }

        const neighborViewContainerId = this.findNeighboringViewContainer(originViewContainer, direction);

        if (neighborViewContainerId === null) {
            // no neighbor in that direction -> split
            const newViewContainer = this.splitViewContainer(originViewContainer, direction);
            this.moveViewToViewContainer(viewId, newViewContainer);
        } else {
            // neighbor found -> move
            this.moveViewToViewContainer(viewId, neighborViewContainerId);
        }
    }

    private moveViewToViewContainer(viewId: ViewId, targetViewContainerId: ViewContainerId): void {
        const oldViewContainerId = this.viewsToViewContainers[viewId];
        if (!oldViewContainerId) {
            throw new Error(`View ${viewId} not registered.`);
        }
        if (!this.registeredViewContainers[targetViewContainerId]) {
            throw new Error(`View container ${targetViewContainerId} not registered.`);
        }
        if (oldViewContainerId === targetViewContainerId) {
            return;
        }

        const oldViewContainerState = this.viewContainerStates[oldViewContainerId].getValue();
        const newViewContainerState = this.viewContainerStates[targetViewContainerId].getValue();

        this.viewContainerStates[oldViewContainerId].next({
            ...oldViewContainerState,
            views: oldViewContainerState.views.filter(v => v !== viewId)
        });
        this.viewContainerStates[targetViewContainerId].next({
            ...newViewContainerState,
            views: [...newViewContainerState.views, viewId]
        });

        this.viewsToViewContainers[viewId] = targetViewContainerId;
    }

    //
    // Split View Handling
    //

    /**
     * Split the specified view container in the specified direction.
     * Returns the id of the new sibling view container.
     */
    splitViewContainer(toSplit: ViewContainerId, direction: WorkbenchDirection): ViewContainerId {

        // create new split view container
        // create new sibling view container
        // add both containers (old and new) to split view container
        // add split view container to parent of old view container

        const toSplitViewContainerState = this.viewContainerStates[toSplit].getValue();
        const toSplitViewContainerConfig = toSplitViewContainerState.viewContainerConfig;

        if (!isNormalViewContainerConfig(toSplitViewContainerConfig)) {
            // The user only ever interacts with normal view containers (i.e. these are the leaves of the vc tree),
            // so there should be no need to split a split view container.
            throw new Error(`View container ${toSplit} is not a normal view container and cannot be split.`);
        }

        if (isNormalViewContainerConfig(toSplitViewContainerConfig) && !toSplitViewContainerConfig.splitAllowed) {
            throw new Error(`View container ${toSplit} does not allow splitting. Should not happen.`);
        }

        const parentSplitViewContainerId = toSplitViewContainerState.parentSplitViewContainer;
        if (!!parentSplitViewContainerId && !this.registeredSplitViewContainers[parentSplitViewContainerId]) {
            throw new Error(`View container ${toSplit} has parent split view container but that's not registered. Should not happen.`);
        }

        const newSiblingViewContainerId = this.findAvailableViewContainerId(toSplit + '-' + direction);
        const newSiblingViewContainerConfig: NormalViewContainerConfig = {
            type: 'NormalViewContainer',
            id: newSiblingViewContainerId,
            splitAllowed: true,
            moveAllowed: false,
            hideAllowed: false,
            allowedPanels: toSplitViewContainerConfig.allowedPanels, // inherit
            displayMode: 'tabbed', // in split view containers, views are always displayed in tabs
        };

        const parentSplitViewContainerConfig = parentSplitViewContainerId && this.registeredSplitViewContainers[parentSplitViewContainerId];
        if (!!parentSplitViewContainerConfig && (
            (parentSplitViewContainerConfig.orientation === 'horizontal' && (direction === 'left' || direction === 'right')) ||
            parentSplitViewContainerConfig.orientation === 'vertical' && (direction === 'up' || direction === 'down'))
        ) {
            // we are a child of a horizontal split view container and want to split horizontally, or we are a child of a vertical split view container and want to split vertically
            // => we are just inserting a new child view container to the parent split view container

            this.registerViewContainer(newSiblingViewContainerConfig, parentSplitViewContainerId);

            const parentSplitViewContainerState = this.splitViewContainerStates[parentSplitViewContainerId].getValue();
            this.splitViewContainerStates[parentSplitViewContainerId].next({
                ...parentSplitViewContainerState,
                children: [...parentSplitViewContainerState.children, newSiblingViewContainerId]
            });
        } else {
            // we are splitting in cross-direction or have no parent SplitViewContainer => need new SplitViewContainer

            const newSplitViewContainerId = toSplit + '-split-' + ++this.splitIdCounter;
            const children: ViewContainerId[] = [];
            if (direction === 'right' || direction === 'down') {
                children.push(toSplit, newSiblingViewContainerId);
            } else {
                children.push(newSiblingViewContainerId, toSplit);
            }

            const newSplitViewContainerConfig: SplitViewContainerConfig = {
                type: 'SplitViewContainer',
                id: newSplitViewContainerId,
                orientation: direction === 'left' || direction === 'right' ? 'horizontal' : 'vertical',
            };

            this.registerViewContainer(newSiblingViewContainerConfig, newSplitViewContainerId);

            this.registerSplitViewContainer(newSplitViewContainerConfig, children, toSplit, parentSplitViewContainerId);

            this.viewContainerStates[toSplit].next({
                ...toSplitViewContainerState,
                parentSplitViewContainer: newSplitViewContainerId,
                inPanel: undefined, // view containers in split views are not associated with panels
            });

            if (!!toSplitViewContainerState.parentSplitViewContainer) {
                // we are a child of a split view container
                const parentSplitViewContainerState = this.splitViewContainerStates[toSplitViewContainerState.parentSplitViewContainer].getValue();
                const oldChildIndex = parentSplitViewContainerState.children.indexOf(toSplit);
                const newChildren = [...parentSplitViewContainerState.children];
                newChildren.splice(oldChildIndex, 1, newSplitViewContainerId);
                this.splitViewContainerStates[toSplitViewContainerState.parentSplitViewContainer].next({
                    ...parentSplitViewContainerState,
                    children: newChildren
                });
            } else {
                // we are a root view container in the panel
                if (!toSplitViewContainerState.inPanel) {
                    console.warn('root view container has not panel. Should not happen.');
                } else {
                    this.showSplitViewContainerInPanel(newSplitViewContainerId, toSplitViewContainerState.inPanel);
                }
            }
        }

        // the new view container gets the focus
        this.activateViewContainer(newSiblingViewContainerId);
        return newSiblingViewContainerId;
    }

    private destroySplitViewContainer(splitViewContainerId: ViewContainerId) {
        this.splitViewContainerStates[splitViewContainerId].complete();

        const component = this.splitViewContainerStates[splitViewContainerId].getValue().componentRef;
        component.destroy();
        delete this.splitViewContainerStates[splitViewContainerId];
        delete this.registeredSplitViewContainers[splitViewContainerId];
    }

    private maintainSplitViewContainers() {
        Object.keys(this.splitViewContainerStates).forEach((splitContainerId) => {
            const splitContainerState = this.splitViewContainerStates[splitContainerId].getValue();
            if (splitContainerState.children.length === 0) {
                // this actually should not happen as a split view container should always have at least one child after user operations
                // delete this.splitViewContainerStates[splitContainerId];
                // delete this.registeredSplitViewContainers[splitContainerId];
            } else if (splitContainerState.children.length === 1) {
                // if we have only one child, we remove the split view container and move the child up
                // "up" means here that we move the child to the parent of the split view container. If there is no parent, we move it to the panel.
                // there is no need to do this recursively as by construction the parent will have more than one child after this operation
                const childId = splitContainerState.children[0];
                const parentSplitContainerId = splitContainerState.parentSplitViewContainer;
                if (!!parentSplitContainerId) {
                    // we have a parent split view container
                    const parentSplitContainerState = this.splitViewContainerStates[parentSplitContainerId].getValue();
                    const oldChildIndex = parentSplitContainerState.children.indexOf(splitContainerId);
                    const newChildren = [...parentSplitContainerState.children];
                    newChildren.splice(oldChildIndex, 1, childId);
                    this.splitViewContainerStates[parentSplitContainerId].next({
                        ...parentSplitContainerState,
                        children: newChildren
                    });
                    if (this.viewContainerStates[childId]) {
                        this.viewContainerStates[childId].next({
                            ...this.viewContainerStates[childId].getValue(),
                            parentSplitViewContainer: parentSplitContainerId
                        });
                    } else if (this.splitViewContainerStates[childId]) {
                        this.splitViewContainerStates[childId].next({
                            ...this.splitViewContainerStates[childId].getValue(),
                            parentSplitViewContainer: parentSplitContainerId
                        });
                    }
                    this.destroySplitViewContainer(splitContainerId);
                } else {
                    // we are a root view container in the panel
                    if (!splitContainerState.inPanel) {
                        console.warn('root view container has not panel. Should not happen.');
                    } else {
                        if (this.viewContainerStates[childId]) {
                            this.showViewContainerInPanel(childId, splitContainerState.inPanel);
                        } else if (this.splitViewContainerStates[childId]) {
                            this.showSplitViewContainerInPanel(childId, splitContainerState.inPanel);
                        }
                    }
                    this.destroySplitViewContainer(splitContainerId);
                }
            }
        });


    }

    private showSplitViewContainerInPanel(splitViewContainerId: ViewContainerId, panelId: PanelId) {
        if (!this.panelStates[panelId]) {
            throw new Error(`Panel with id ${panelId} not registered.`);
        }
        if (!this.splitViewContainerStates[splitViewContainerId]) {
            throw new Error(`No state for split view container with id ${splitViewContainerId}.`);
        }

        const viewContainerState = this.splitViewContainerStates[splitViewContainerId].getValue();
        if (!!viewContainerState.inPanel && viewContainerState.inPanel !== panelId) {
            throw new Error(`Split view containers must not be moved between panels.`);
        }

        this.splitViewContainerStates[splitViewContainerId].next({
            ...this.splitViewContainerStates[splitViewContainerId].getValue(),
            inPanel: panelId
        });
        this.panelStates[panelId].next({
            panelId: panelId,
            shownViewContainer: splitViewContainerId,
        });
    }

    //
    // Additional View Manipulations
    //

    /**
     * This does not change the name of the view permanently. Only until it is loaded again.
     *
     * @param viewId ID of the view to change
     * @param newViewName new name of the view
     */
    changeViewName(viewId: ViewId, newViewName: string): void {
        const viewConfig = this.registeredViews[viewId];

        if (viewConfig) {
            viewConfig.name = newViewName.trim();
        } else {
            console.warn(`Unable to change View Name, view with id ${viewId} not found`);
        }
    }

    setViewExpansionState(viewId: ViewId, expanded: boolean): void {
        this.viewStates[viewId].next({
            ...this.viewStates[viewId].getValue(),
            expanded: expanded
        });
    }

    setViewContainerActiveTab(viewContainerId: ViewContainerId, activeTab: ViewId) {
        this.setLastActive(activeTab);
        this.viewContainerStates[viewContainerId].next({
            ...this.viewContainerStates[viewContainerId].getValue(),
            activeTab: activeTab
        });
        this.persistLayoutState();
        this.triggerLayoutChangedEvent();
    }

    private setLastActive(viewId: ViewId) {
        this.registeredViews[viewId].lastActive = Date.now();
    }

    getOldestView(viewContainerId: ViewContainerId) {
        const candidates = Object.values(this.registeredViews).filter(v => v.defaultViewContainer === viewContainerId && !!v.lastActive);
        if (candidates.length === 0) {
            return null;
        } else {
            return candidates.reduce((min, obj) => obj.lastActive < min.lastActive ? obj : min, candidates[0]);
        }
    }

    /**
     * Collapses all views from ViewContainer except the provided view.
     *
     * @param viewId view that should be expanded
     * @param parentViewContainer ViewContainer to check
     */
    setExclusiveViewExpansionState(viewId: ViewId, parentViewContainer: ViewContainerId): void {
        const childViews: string[] = this.getAllChildViewsOfViewContainer(parentViewContainer);

        for (const view of childViews) {
            this.setViewExpansionState(view, view === viewId);
        }
    }

    //
    // Public Getters
    //

    /**
     * Low-level access to a view for those who really need it. Handle with care.
     */
    getViewDomElement(viewId: ViewId): HTMLElement | undefined {
        if (!this.viewStates[viewId]?.getValue()?.viewComponentRef) {
            return undefined;
        }
        return this.viewStates[viewId].getValue().viewComponentRef.location.nativeElement;
    }

    getViewConfig(viewId: ViewId): WorkbenchViewConfig<any> {
        return this.registeredViews[viewId];
    }

    getVisibleViewConfigsForViewContainer(viewContainerId: ViewContainerId): WorkbenchViewConfig<any>[] {
        const state = this.viewContainerStates[viewContainerId]?.getValue();
        return (state?.views || [])
            .filter(viewId => this.isViewVisibleInCurrentMode(viewId))
            .map(viewId => this.registeredViews[viewId])
            .filter((view): view is WorkbenchViewConfig<any> => !!view);
    }

    getViewState(viewId: ViewId): ViewState {
        return this.viewStates[viewId].getValue();
    }

    getViewContainerIdForView(viewId: ViewId): ViewContainerId | undefined {
        return this.viewsToViewContainers[viewId];
    }

    /**
     * Find all child views from the ViewContainer
     *
     * @param viewContainerId ViewContainer to check
     * @returns list of children view IDs
     */
    getAllChildViewsOfViewContainer(viewContainerId: ViewContainerId): string[] {
        const childViews: string[] = [];

        for (const view in this.viewStates) {
            if (this.viewsToViewContainers[view] === viewContainerId) {
                childViews.push(view);
            }
        }

        return childViews;
    }


    isViewContainerShown(viewContainerId: ViewContainerId): boolean {
        const checkViewContainerEqualsOrContains = (currentTarget: ViewContainerId, toSearch: ViewContainerId): boolean => {
            if (currentTarget === toSearch) {
                return true;
            } else if (this.splitViewContainerStates[currentTarget]) {
                // The target is a split view container, check if one of its children is the view container we are looking for
                const splitViewContainerState = this.splitViewContainerStates[currentTarget].getValue();
                return _.some(splitViewContainerState.children, (vcid) => checkViewContainerEqualsOrContains(vcid, toSearch));
            }
            // If the current target is not equal, nor a split view we are sure it's not the one we are looking for
            return false;
        };

        for (const panelId of Object.keys(this.panelStates)) {
            const panelState: PanelState = this.panelStates[panelId]?.getValue();
            if (checkViewContainerEqualsOrContains(panelState.shownViewContainer, viewContainerId)) {
                return true;
            }
        }
        return false;
    }

    getPanelSizes () : PanelSizes {
        return this.persistentLayoutState.sizes;
    }

    //
    // Event Publishing
    //

    /**
     * Return event stream for the specified view (or any view if viewId is not specified).
     */
    subscribeToViewEvents$(viewId?: ViewId, skipInit: boolean = true): Observable<ViewState> {
        if (!viewId) {
            if (skipInit) {
                return this.viewsChanged$.pipe(switchMap(() => merge(...Object.values(this.viewStates).map(view => view.asObservable().pipe(skip(1)) /* skip initial value */))));
            } else {
                return this.viewsChanged$.pipe(switchMap(() => merge(...Object.values(this.viewStates).map(view => view.asObservable()))));
            }
        } else {
            if (!this.viewStates[viewId]) {
                throw new Error(`View with id ${viewId} not registered.`);
            }
            if (skipInit) {
                return this.viewsChanged$.pipe(switchMap(() => this.viewStates[viewId].asObservable().pipe(skip(1)) /* skip initial value */));
            } else {
                return this.viewsChanged$.pipe(switchMap(() => this.viewStates[viewId].asObservable()));
            }
        }
    }

    /**
     * Return event stream for the specified panel (or any panel if panelId is not specified).
     */
    subscribeToPanelEvents$(panelId?: PanelId, skipInit: boolean = true): Observable<PanelState> {
        if (!panelId) {
            if (skipInit) {
                return merge(...Object.values(this.panelStates).map(panel => panel.asObservable().pipe(skip(1)) /* skip initial value */));
            } else {
                return merge(...Object.values(this.panelStates).map(panel => panel.asObservable()));
            }
        } else {
            if (!this.panelStates[panelId]) {
                throw new Error(`Panel with id ${panelId} not registered.`);
            }
            if (skipInit) {
                return this.panelStates[panelId].asObservable().pipe(skip(1)) /* skip initial value */;
            } else {
                return this.panelStates[panelId].asObservable();
            }
        }
    }

    subscribeToToolbarContainerEvents$(toolbarContainerId?: ToolbarContainerId, skipInit: boolean = false): Observable<ToolbarContainerState> {
        if (!toolbarContainerId) {
            if (skipInit) {
                return merge(...Object.values(this.toolbarContainersStates).map(container => container.asObservable().pipe(skip(1)) /* skip initial value */));
            } else {
                return merge(...Object.values(this.toolbarContainersStates).map(container => container.asObservable()));
            }
        } else {
            if (!this.toolbarContainersStates[toolbarContainerId]) {
                throw new Error(`Toolbar container with id '${toolbarContainerId}' not registered.`);
            }
            if (skipInit) {
                return this.toolbarContainersStates[toolbarContainerId].asObservable().pipe(skip(1)) /* skip initial value */;
            } else {
                return this.toolbarContainersStates[toolbarContainerId].asObservable();
            }
        }
    }

    subscribeActiveContainerId$(): Observable<ViewContainerId> {
        return this.activeViewContainerId$.asObservable();
    }

    subscribeToViewContainerChanges$(id: ViewContainerId): Observable<NormalViewContainerState | SplitViewContainerState> {
        // this logic is a bit ugly, but I don't think it has any negative consequences
        if (this.viewContainerStates[id]) {
            return this.viewContainerStates[id].asObservable();
        } else if (this.splitViewContainerStates[id]) {
            return this.splitViewContainerStates[id].asObservable();
        } else {
            throw new Error(`View container with id ${id} not registered.`);
        }
    }

    subscribeToNormalViewContainerChanges$(id: ViewContainerId): Observable<NormalViewContainerState> {
        return this.subscribeToViewContainerChanges$(id).pipe(filter((state): state is NormalViewContainerState => state.type === 'NormalViewContainerState'));
    }

    subscribeActiveViewId$(): Observable<ViewId> {
        return this.activeViewId$.asObservable();
    }

    //
    // Heuristics for Locations and Naming
    //

    /**
     * Return neighbor of the specified view container in the specified direction.
     * Returns null if there is no neighbor in that direction.
     */
    private findNeighboringViewContainer(from: ViewContainerId, direction: WorkbenchDirection): ViewContainerId | null {
        const fromViewContainerState = this.viewContainerStates[from].getValue();
        if (!fromViewContainerState.parentSplitViewContainer) {
            // if we are not in a splitcontainer, then there is definitely no neighbor
            return null;
        }

        const parentSplitViewContainerState = this.splitViewContainerStates[fromViewContainerState.parentSplitViewContainer].getValue();
        const parentConfig = parentSplitViewContainerState.viewContainerConfig;
        const parentOrientation = parentConfig.orientation;

        // this is a little convoluted, but I did not want to duplicate the code for horizontal and vertical
        const orientationDirections = {
            horizontal: ['left', 'right'],
            vertical: ['up', 'down']
        };

        for (const [orientation, directions] of Object.entries(orientationDirections)) {
            if (parentOrientation === orientation && (direction === directions[0] || direction === directions[1])) {
                // if our neighbor direction is the same as the parent orientation, we are in the same split view container.
                // so we need to find the neighbor in the same split view container

                // find our index in the parent split view container
                const fromIndex = parentSplitViewContainerState.children.indexOf(from);
                if (direction === directions[0]) {
                    if (fromIndex === 0) {
                        // we are the first child (left-most, top-most), so no neighbor in left or up direction
                        return null;
                    } else {
                        return parentSplitViewContainerState.children[fromIndex - 1];
                    }
                } else {
                    if (fromIndex === parentSplitViewContainerState.children.length - 1) {
                        // we are the last child, so no neighbor
                        return null;
                    } else {
                        return parentSplitViewContainerState.children[fromIndex + 1];
                    }
                }
            }
        }

        // if we are here, we are not in the same split view container as our neighbor, or this is a move in the cross axis,
        // for which a new split view container must be created (but not by this function).
        return null;
    }

    private mapPanelIdToTClosestToolbarId(panelId: PanelId): ToolbarId {
        if (panelId === 'bottom') {
            return 'left-bottom';
        } else if (panelId === 'center-center') {
            return 'top-left';
        } else {
            return panelId;
        }
    }

    private findAvailableViewContainerId(prefix: ViewContainerId): ViewContainerId {
        let i = 0;
        while (!!this.registeredViewContainers[prefix + '-' + i]) {
            i++;
        }
        return prefix + '-' + i;
    }

    //
    // Resize Handling
    //

    // This function simulates a browser resize event and should be used in cases where the general layout changes,
    // i.e. a new panel is displayed or hidden. That way, the recalculation of any shown views can still be based on the
    // resize event, that they need to handle anyway if necessary.
    triggerLayoutChangedEvent() {
        window.dispatchEvent(new Event('resize'));
    }

    isViewRegistered(viewId: string): boolean {
        return !!this.registeredViews[viewId];
    }
}
